import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithPopup,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);

  const API_BASE_URL = (() => {
    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
    if (process.env.NODE_ENV === 'development') return 'http://localhost:8000';
    return '';
  })();

  // Get Firebase ID token for API calls
  const getIdToken = async () => {
    if (!user) return null;
    try {
      return await user.getIdToken();
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  };

  // Fetch user profile from backend
  const fetchUserProfile = async (firebaseUser) => {
    try {
      const token = await firebaseUser.getIdToken();
      console.log('Fetching user profile from:', `${API_BASE_URL}/api/auth/me`);
      
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('User profile fetched:', data);
        setUserProfile(data);
        return data;
      } else if (response.status === 404) {
        // User not found in DB, sync will create them
        console.log('User not found, syncing...');
        const syncResponse = await fetch(`${API_BASE_URL}/api/auth/sync`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (syncResponse.ok) {
          const data = await syncResponse.json();
          console.log('User synced:', data);
          setUserProfile(data);
          return data;
        } else {
          console.error('Sync failed:', await syncResponse.text());
        }
      } else {
        console.error('Fetch profile failed:', response.status, await response.text());
      }
      return null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        await fetchUserProfile(firebaseUser);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Sign up with email and password
  const signUp = async (email, password, displayName) => {
    setAuthLoading(true);
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update display name
      if (displayName) {
        await updateProfile(result.user, { displayName });
      }
      
      // Send email verification
      await sendEmailVerification(result.user);
      
      // Sync user to backend
      const token = await result.user.getIdToken();
      await fetch(`${API_BASE_URL}/api/auth/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      await fetchUserProfile(result.user);
      return { success: true, user: result.user };
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: getFirebaseErrorMessage(error.code) };
    } finally {
      setAuthLoading(false);
    }
  };

  // Sign in with email and password
  const signIn = async (email, password) => {
    setAuthLoading(true);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Sync user to backend (updates lastLoginAt)
      const token = await result.user.getIdToken();
      await fetch(`${API_BASE_URL}/api/auth/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      await fetchUserProfile(result.user);
      return { success: true, user: result.user };
    } catch (error) {
      console.error('Signin error:', error);
      return { success: false, error: getFirebaseErrorMessage(error.code) };
    } finally {
      setAuthLoading(false);
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    setAuthLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      
      // Sync user to backend and get profile with credits
      const token = await result.user.getIdToken();
      const syncResponse = await fetch(`${API_BASE_URL}/api/auth/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (syncResponse.ok) {
        const userData = await syncResponse.json();
        setUserProfile(userData);
      } else {
        console.error('Sync failed:', await syncResponse.text());
        // Try fetching profile anyway
        await fetchUserProfile(result.user);
      }
      
      return { success: true, user: result.user };
    } catch (error) {
      console.error('Google signin error:', error);
      return { success: false, error: getFirebaseErrorMessage(error.code) };
    } finally {
      setAuthLoading(false);
    }
  };

  // Sign out
  const logout = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      console.error('Password reset error:', error);
      return { success: false, error: getFirebaseErrorMessage(error.code) };
    }
  };

  // Resend email verification
  const resendVerificationEmail = async () => {
    if (!user) return { success: false, error: 'No user logged in' };
    try {
      await sendEmailVerification(user);
      return { success: true };
    } catch (error) {
      console.error('Resend verification error:', error);
      return { success: false, error: getFirebaseErrorMessage(error.code) };
    }
  };

  // Refresh user profile (call after credit usage, etc.)
  const refreshUserProfile = async () => {
    if (user) {
      await fetchUserProfile(user);
    }
  };

  // Get user-friendly error messages
  const getFirebaseErrorMessage = (errorCode) => {
    const errorMessages = {
      'auth/email-already-in-use': 'This email is already registered. Please sign in instead.',
      'auth/invalid-email': 'Please enter a valid email address.',
      'auth/operation-not-allowed': 'This sign-in method is not enabled.',
      'auth/weak-password': 'Password should be at least 6 characters.',
      'auth/user-disabled': 'This account has been disabled.',
      'auth/user-not-found': 'No account found with this email.',
      'auth/wrong-password': 'Incorrect password. Please try again.',
      'auth/invalid-credential': 'Invalid email or password.',
      'auth/too-many-requests': 'Too many attempts. Please try again later.',
      'auth/popup-closed-by-user': 'Sign-in cancelled. Please try again.',
      'auth/cancelled-popup-request': 'Sign-in cancelled.',
      'auth/popup-blocked': 'Pop-up blocked. Please allow pop-ups for this site.',
    };
    return errorMessages[errorCode] || 'An error occurred. Please try again.';
  };

  const value = {
    user,
    userProfile,
    loading,
    authLoading,
    signUp,
    signIn,
    signInWithGoogle,
    logout,
    resetPassword,
    resendVerificationEmail,
    refreshUserProfile,
    getIdToken,
    isAuthenticated: !!user,
    credits: userProfile?.credits?.available ?? 0,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
